import { Injectable } from '@angular/core';
import { getIDType } from '@datorama/akita';
import { UntilDestroy } from '@ngneat/until-destroy';
import { environment } from '@y42/environment';
import { METADATA_JSON_SCHEMA_URL, Y42MetadataGitModel } from '@y42/feature/json-schema';
import { Y42RouteDetailTabs, Y42RouterService, Y42Routes } from '@y42/feature/routing';
import { GitQuery } from '@y42/git';
import { ImpexService, TriggerService } from '@y42/impex';
import { Y42FeatureType } from '@y42/json-schema';
import { ToastService } from '@y42/toast';
import { getSafeString } from '@y42/util/safe-string';
import { Subject } from 'rxjs';
import {
  CreateExportParams,
  ExportGitService,
  ExportQuery,
  ExportSettingsGitModel,
  ExportState,
  ExportStore,
  ExportStoreModel,
  ExportTriggerJobStore,
  ExportTriggerQuery,
  ExportTriggerStore,
  ExportTypeEnum,
} from '../../state';
import { ExportTriggerJobService } from '../export-trigger-job';
import { ExportAclService } from './export-acl.service';
import { ExportUserDataService } from './export-user-data.service';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class ExportService extends ImpexService<ExportState> {
  constructor(
    protected readonly userData: ExportUserDataService,
    protected readonly store: ExportStore,
    protected readonly query: ExportQuery,
    protected readonly git: ExportGitService,
    protected readonly triggerStore: ExportTriggerStore,
    protected readonly triggerQuery: ExportTriggerQuery,
    protected readonly jobStore: ExportTriggerJobStore,
    protected readonly acl: ExportAclService,
    protected readonly routerService: Y42RouterService,
    protected readonly toast: ToastService,
    protected readonly gitQuery: GitQuery,
    protected readonly jobService: ExportTriggerJobService,
    protected readonly triggerService: TriggerService,
  ) {
    super(
      {
        featureType: Y42FeatureType.Export,
        store,
        query,
        acl,
        userData,
        git,
        gitQuery,
        toast,
        childStores: [triggerStore],
      },
      triggerService,
    );
  }

  public readonly deselectDetailStructureList$ = new Subject<boolean>();

  override async getAll(options?: { setLoading?: boolean | undefined } | undefined): Promise<void> {
    if (options?.setLoading) {
      this.store.setLoading(true);
    }

    await super.getAll({ setLoading: false });

    if (options?.setLoading) {
      this.store.setLoading(false);
    }
  }

  override async get($name: string, options?: { ignoreErrors?: boolean | undefined } | undefined) {
    const exxport = await super.get($name, options);

    return exxport;
  }

  async create({ title, description, typeModel }: CreateExportParams): Promise<void> {
    const $name = getSafeString(title);
    const exxport = this.store.create({
      ...new Y42MetadataGitModel(),
      $schema: METADATA_JSON_SCHEMA_URL,
      $name,
      name: title,
      description,
      settings: {
        $schema: this.getUrl(typeModel.type, 'source'),
      } as ExportSettingsGitModel,
    });
    await this.store.pending;

    await this.git.commitChanges(`exports: new export "${$name}" created`, exxport.$path);

    await this.routerService.navigateToDetail(Y42Routes.Export, $name, Y42RouteDetailTabs.Overview);
  }

  async update(
    item: Partial<ExportStoreModel>,
    $path: ExportStoreModel['$path'] = this.query.getActiveId()!,
  ): Promise<void> {
    if (!$path) {
      throw new Error(`Export "${$path}" not found.`);
    }

    this.store.update($path, item);
    await this.store.pending;
  }

  async delete($path: string = this.query.getActiveId() as getIDType<ExportState>) {
    const entity = this.query.getEntity($path);
    this.store.delete($path);
    await this.store.pending;
    await this.git.commitChanges(`exports: export '${entity?.$name ?? $path}' deleted`, [$path]);
  }

  async deleteAuthorization() {
    const name = this.query.getActive()!.$name;
    await this.git.deleteExportAuthorization(name);
  }

  getUrl(type: ExportTypeEnum, suffix: 'source' | 'secret' | 'table') {
    // TODO figure out versioning
    return `${environment.url.json_schema}/integrations/exports/${type}/0.0.0/${suffix}.json`;
  }
}
